import { getTierPrice } from '@/lib/pricing';
import type { AddOn, Company, Service } from '../types';

const round2 = (value: number) => Math.round(value * 100) / 100;

const formatMoney = (value: number, currency?: string) => {
  const symbol = !currency || currency === 'GBP' ? '£' : `${currency} `;
  return `${symbol}${value.toFixed(2)}`;
};

/**
 * Client-side estimate of the booking total after a service/add-on change,
 * mirroring how the manual-booking flow prices a selection. The server
 * recalculates the authoritative snapshot on save; this exists only to show
 * staff the price impact before saving.
 */
export const estimateAmendedTotal = ({
  service,
  services,
  selectedAddOns,
  company,
  vatRatePercent
}: {
  service?: Service | undefined;
  services?: Service[];
  selectedAddOns: AddOn[];
  company?: Company | null;
  vatRatePercent?: number;
}): number | null => {
  const selectedServices = services ?? (service ? [service] : []);
  if (selectedServices.length === 0) return null;
  const priceOptions = { company: company || null, isCorporate: Boolean(company) };
  let subtotal = 0;
  selectedServices.forEach((s) => {
    subtotal += getTierPrice(s, priceOptions);
  });
  selectedAddOns.forEach((a) => {
    subtotal += getTierPrice(a, priceOptions);
  });
  const rate = Number(vatRatePercent);
  const vat = Number.isFinite(rate) && rate > 0 ? subtotal * (rate / 100) : 0;
  return round2(subtotal + vat);
};

/**
 * Money-mismatch warning for the booking editor. There is no automatic
 * refund/extra-charge on a service change — the server rewrites the pricing
 * snapshot but never touches the payments ledger — so staff must settle any
 * difference manually in the payment provider.
 */
export const buildPaymentAmendWarning = ({
  paymentMethod,
  paymentStatus,
  originalTotal,
  estimatedTotal,
  currency
}: {
  paymentMethod: string;
  paymentStatus?: string;
  originalTotal?: number;
  estimatedTotal: number | null;
  currency?: string;
}): string | null => {
  if (paymentMethod !== 'card' || paymentStatus !== 'captured') return null;
  if (estimatedTotal == null || originalTotal == null) return null;
  const paid = round2(Number(originalTotal));
  const next = round2(estimatedTotal);
  if (!Number.isFinite(paid) || paid === next) return null;
  const paidText = formatMoney(paid, currency);
  const nextText = formatMoney(next, currency);
  const diffText = formatMoney(Math.abs(paid - next), currency);
  return paid > next
    ? `Customer paid ${paidText} by card. New total is ${nextText} — refund ${diffText} manually via the payment provider.`
    : `Customer paid ${paidText} by card. New total is ${nextText} — collect the additional ${diffText} manually.`;
};

/** Selected add-ons that remain valid for the newly chosen service selection. */
export const pruneIncompatibleAddOns = (
  addOnIds: string[],
  addOns: AddOn[],
  serviceIdOrIds: string | string[]
): string[] => {
  const serviceIds = Array.isArray(serviceIdOrIds) ? serviceIdOrIds : [serviceIdOrIds];
  const byId = new Map(addOns.map((a) => [a.id, a]));
  return addOnIds.filter((id) =>
    Boolean(byId.get(id)?.compatibleServiceIds.some((sid) => serviceIds.includes(sid)))
  );
};
